import { ONBOARDING_GRANT_PREFIX } from '../api/onboarding';

// ─────────────────────────────────────────────────────────────────────
// WHERE THE ONBOARDING GRANT LIVES (#710).
//
// IN MEMORY, AND NOWHERE ELSE. That is a decision, not an omission, and it
// follows the credential's own design:
//
//   NOT A COOKIE — the API refuses a grant that arrives as one, which is what
//   removes the whole CSRF question from the endpoint it reaches
//   (`auth/onboardingMiddleware.ts`). Storing it as one here would be storing
//   something the server will not accept.
//
//   NOT IN `localStorage` OR `sessionStorage` — a thirty-minute door does not
//   need to survive a reload, and a credential in storage is one an XSS can
//   read at leisure. The cost of losing it is one e-mail: the registration door
//   is idempotent, so asking again for a code is the ordinary motion.
//
//   NOT IN `Storage` — that is the `/kv` backend, which is club-scoped and
//   would be a credential sent to the server to keep on our behalf.
//
// So a reload during onboarding returns to the e-mail step. The screen says so
// rather than failing at the next call.
//
// ── IT IS DELIBERATELY NOT A SESSION ───────────────────────────────
//
// No `signIn`, no `adopt`, no listeners, no restore-from-cookie. `clubSession`
// and `memberSession` have all of that because they are places to live; this is
// a door somebody walks through once, and giving it a session's shape would
// invite it to be treated as one.
// ─────────────────────────────────────────────────────────────────────

export interface OnboardingGrantState {
  /** The player this grant belongs to, for the screens that say «you». */
  player: { id: string; name: string };
  /** When it stops working. Milliseconds since the epoch. */
  expiresAt: number | null;
}

export interface OnboardingGrantHolder {
  /** The raw token, or null when there is none or it has expired. */
  token(): string | null;
  state(): OnboardingGrantState | null;
  hold(input: { grant: string; expiresAt: string; player: { id: string; name: string } }): void;
  /** Forget it — after joining, or when the server says it is gone. */
  release(): void;
}

export function createOnboardingGrant(now: () => number = () => Date.now()): OnboardingGrantHolder {
  let token: string | null = null;
  let state: OnboardingGrantState | null = null;

  /** Expiry is checked on READ, so a grant cannot be used past its window. */
  const live = (): boolean => {
    if (token === null) return false;
    if (state?.expiresAt !== null && state !== null && state.expiresAt <= now()) return false;
    return true;
  };

  return {
    token() {
      if (!live()) {
        token = null;
        state = null;
        return null;
      }
      return token;
    },
    state() {
      // THE SAME EXPIRY CHECK, so a screen cannot render «you are registered»
      // from a grant the next call would refuse.
      if (!live()) {
        token = null;
        state = null;
        return null;
      }
      return state;
    },
    hold(input) {
      // REFUSES ANYTHING THAT IS NOT A GRANT. A club or member token held here
      // would be sent to the onboarding endpoint, where it is refused on its
      // prefix — but the honest place to stop is before it is stored.
      if (!input.grant.startsWith(ONBOARDING_GRANT_PREFIX)) return;
      const expires = Date.parse(input.expiresAt);
      token = input.grant;
      state = {
        player: input.player,
        expiresAt: Number.isFinite(expires) ? expires : null,
      };
    },
    release() {
      token = null;
      state = null;
    },
  };
}
